import { Observable, of } from 'rxjs';
import { map, tap, switchMap, take } from 'rxjs/operators';

import { IRole } from '../models';
import {
  PageConfig,
  Endpoint,
  CentralApis,
  ACTIONS,
} from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';
import { Role } from '../models/role.model';
import { Result } from '../interfaces/helpers';
import { Entitlement } from '../models/entitlement';

export class RolePage extends Page<Role> {
  endpoint = '';

  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super((endpoint as CentralApis).iam + 'userRole', PageTitles.role, config);
    this.endpoint = (endpoint as CentralApis).iam;

    this.initializeColumns({
      keys: new Role(),
      editableFields: this.editableFields,
      hiddenTableFields: this.hiddenFields,
      hiddenFormFields: this.hiddenFields,
    });
  }

  get actions(): string[] {
    return [ACTIONS.delete];
  }

  get hiddenFields(): string[] {
    return [
      'createdAt',
      'modifiedAt',
      'createdBy',
      'modifiedBy',
      'href',
      'entitlement',
    ];
  }

  get editableFields(): string[] {
    return ['description', 'active'];
  }

  setUrl() {
    this.url = this.endpoint + 'userRole';
  }

  fetch(queryString?): Observable<Result<Role>> {
    return this.getItems(queryString).pipe(
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }

  fetchById(id: string): Observable<Role> {
    return this.getItem(id).pipe(
      map((data: IRole) => this.createFromDTO(data))
    );
  }

  update(item: IRole): Observable<Role> {
    let updatedRoles: Role[];

    return this.items.pipe(
      take(1),
      switchMap((roles) => {
        if (!roles || roles.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(roles);
        }
      }),
      switchMap((roles) => {
        const roleIndex = roles.findIndex((i) => i.id === item.id);
        updatedRoles = [...roles];
        const oldRole = updatedRoles[roleIndex];
        updatedRoles[roleIndex] = new Role({
          ...item,
          id: oldRole.id,
        });
        return this.updateItems(updatedRoles[roleIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedRoles);
      })
    );
  }

  createFromDTO(item): Role {
    const entitlements = !item.entitlement
      ? []
      : item.entitlement.map((entitlement) => new Entitlement(entitlement));

    return new Role({
      ...item,
      description: item.description === null ? '' : item.description,
      entitlement: entitlements,
    });
  }
}
